import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { authApi } from '../api/auth'
import { currentTeacherIdStorage, onSessionExpired } from '../api/client'
import type { TeacherDto } from '../api/types'
import { clearClientSession } from './session'

/**
 * login() returns:
 *  - { requireTotp: false, teacher } — успех. Если 2FA обязательна, но не
 *      настроена (6.1), сервер выдал лишь ограниченный токен настройки —
 *      TotpEnforcementGate покажет экран настройки вместо приложения;
 *  - { requireTotp: true, challengeToken } — нужен второй фактор,
 *      вызвать loginTotp(challengeToken, code, trustDevice).
 */
export type LoginResult =
  | { requireTotp: false; teacher: TeacherDto }
  | { requireTotp: true; challengeToken: string }

interface AuthState {
  teacher: TeacherDto | null
  loading: boolean
  login: (email: string, password: string) => Promise<LoginResult>
  loginTotp: (challengeToken: string, code: string, trustDevice?: boolean) => Promise<TeacherDto>
  register: (email: string, password: string, displayName: string, parentInviteToken?: string) => Promise<void>
  logout: () => Promise<void>
  updateProfile: (patch: Partial<Pick<TeacherDto, 'displayName' | 'locale' | 'theme' | 'preferredLlmProvider'>>) => Promise<void>
  refreshTeacher: () => Promise<void>
}

const AuthContext = createContext<AuthState | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [teacher, setTeacher] = useState<TeacherDto | null>(null)
  const [loading, setLoading] = useState(true)
  const queryClient = useQueryClient()

  // Refresh-токен истёк/отозван (logout на другом устройстве, сброс пароля,
  // детект кражи) — чистим состояние, роутер уведёт на экран входа.
  useEffect(() => onSessionExpired(() => {
    clearClientSession(queryClient)
    setTeacher(null)
  }), [queryClient])

  useEffect(() => {
    authApi
      .me()
      .then((t) => {
        currentTeacherIdStorage.set(t.id)
        setTeacher(t)
      })
      .catch(() => {
        currentTeacherIdStorage.set(null)
      })
      .finally(() => setLoading(false))
  }, [])

  const login = useCallback(async (email: string, password: string): Promise<LoginResult> => {
    const resp = await authApi.login({ email, password })
    if (resp.requireTotp) {
      return { requireTotp: true, challengeToken: resp.challengeToken! }
    }
    currentTeacherIdStorage.set(resp.teacher!.id)
    setTeacher(resp.teacher!)
    return { requireTotp: false, teacher: resp.teacher! }
  }, [])

  const loginTotp = useCallback(async (challengeToken: string, code: string, trustDevice = false): Promise<TeacherDto> => {
    const resp = await authApi.loginTotp(challengeToken, code, trustDevice)
    currentTeacherIdStorage.set(resp.teacher!.id)
    setTeacher(resp.teacher!)
    return resp.teacher!
  }, [])

  const register = useCallback(
    async (email: string, password: string, displayName: string, parentInviteToken?: string) => {
      const resp = await authApi.register({ email, password, displayName, parentInviteToken })
      currentTeacherIdStorage.set(resp.teacher.id)
      setTeacher(resp.teacher)
    },
    [],
  )

  const logout = useCallback(async () => {
    try {
      await authApi.logout()
    } catch {
      // даже если backend недоступен, чистим локальное состояние
    }
    // Сброс кэша запросов и курсоров pull: иначе данные (ПДн учеников)
    // предыдущего пользователя остаются в памяти вкладки и видны следующему.
    clearClientSession(queryClient)
    setTeacher(null)
  }, [queryClient])

  const updateProfile = useCallback(
    async (patch: Partial<Pick<TeacherDto, 'displayName' | 'locale' | 'theme' | 'preferredLlmProvider'>>) => {
      const updated = await authApi.updateProfile(patch)
      setTeacher(updated)
    },
    [],
  )

  const refreshTeacher = useCallback(async () => {
    const t = await authApi.me()
    setTeacher(t)
  }, [])

  return (
    <AuthContext.Provider value={{
      teacher, loading, login, loginTotp, register, logout, updateProfile, refreshTeacher,
    }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}
